import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, EmptyState, Field, InlineAlert, LoadingState, SearchField } from '../ui/Primitives';
import { formatDateTime, personName, timestampValue } from '../../utils/display';

const counterpart = (message, currentUserId, users) => {
  const sent = message.senderId === currentUserId;
  const id = sent ? message.recipientId : message.senderId;
  const match = users.find(user => user.id === id);
  return {
    id,
    name: sent
      ? (message.recipientName || (match ? personName(match) : '') || 'Recipient')
      : (message.senderName || (match ? personName(match) : '') || 'Sender'),
    email: sent ? (message.recipientEmail || match?.email || '') : (message.senderEmail || match?.email || ''),
    role: sent ? (message.recipientRole || match?.role || '') : (message.senderRole || match?.role || '')
  };
};

export default function MessageLayout({ currentUser, role, messages = [], users = [], addMessage, updateMessage, recipientRoles = [], loading = false }) {
  const currentUserId = currentUser?.id;
  const [selectedId, setSelectedId] = useState('');
  const [showMobileList, setShowMobileList] = useState(true);
  const [query, setQuery] = useState('');
  const [composeOpen, setComposeOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [reply, setReply] = useState('');
  const [draft, setDraft] = useState({ recipientId: '', subject: '', content: '' });

  const scopedMessages = useMemo(() => messages
    .filter(message => message.senderId === currentUserId || message.recipientId === currentUserId)
    .sort((left, right) => timestampValue(right.createdAt || right.timestamp) - timestampValue(left.createdAt || left.timestamp)), [messages, currentUserId]);

  const visibleMessages = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return scopedMessages;
    return scopedMessages.filter(message => {
      const other = counterpart(message, currentUserId, users);
      return [other.name, other.email, message.subject, message.content]
        .filter(Boolean)
        .some(value => String(value).toLowerCase().includes(needle));
    });
  }, [scopedMessages, query, currentUserId, users]);

  const selected = scopedMessages.find(message => message.id === selectedId) || null;
  const recipients = useMemo(() => {
    const available = new Map();
    users.forEach(candidate => {
      const roles = candidate.roles || (candidate.role ? [candidate.role] : []);
      const allowedRole = roles.find(candidateRole => recipientRoles.includes(candidateRole));
      if (candidate.id !== currentUserId && allowedRole) {
        available.set(candidate.id, { ...candidate, role: allowedRole });
      }
    });
    scopedMessages.forEach(message => {
      const contact = counterpart(message, currentUserId, users);
      if (contact.id && recipientRoles.includes(contact.role) && !available.has(contact.id)) {
        available.set(contact.id, {
          id: contact.id,
          firstName: contact.name,
          lastName: '',
          email: contact.email,
          role: contact.role
        });
      }
    });
    return Array.from(available.values()).sort((left, right) => personName(left).localeCompare(personName(right)));
  }, [users, scopedMessages, currentUserId, recipientRoles]);

  useEffect(() => {
    if (!selectedId && scopedMessages[0]) setSelectedId(scopedMessages[0].id);
    if (selectedId && !scopedMessages.some(message => message.id === selectedId)) {
      setSelectedId(scopedMessages[0]?.id || '');
    }
  }, [scopedMessages, selectedId]);

  useEffect(() => {
    if (!selected || selected.recipientId !== currentUserId || selected.read || !updateMessage) return;
    updateMessage(selected.id, { read: true }).catch(() => {});
  }, [selected, currentUserId, updateMessage]);

  const sendDraft = async () => {
    if (!draft.recipientId || !draft.subject.trim() || !draft.content.trim()) return;
    const recipient = recipients.find(candidate => candidate.id === draft.recipientId);
    if (!recipient) {
      setError('Select an available recipient.');
      return;
    }
    setSending(true);
    setError('');
    try {
      await addMessage({
        senderId: currentUserId,
        senderName: personName(currentUser),
        senderEmail: currentUser?.email || '',
        senderRole: role,
        recipientId: recipient.id,
        recipientName: personName(recipient),
        recipientEmail: recipient.email || '',
        recipientRole: recipient.role,
        subject: draft.subject.trim(),
        content: draft.content.trim(),
        timestamp: new Date().toISOString(),
        read: false,
        thread: []
      });
      setDraft({ recipientId: '', subject: '', content: '' });
      setComposeOpen(false);
    } catch {
      setError('Your message could not be sent. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const sendReply = async () => {
    if (!selected || !reply.trim()) return;
    setSending(true);
    setError('');
    const other = counterpart(selected, currentUserId, users);
    const nextThread = [
      ...(selected.thread || []),
      {
        id: `${Date.now()}`,
        senderId: currentUserId,
        senderName: personName(currentUser),
        senderRole: role,
        recipientId: other.id,
        recipientRole: other.role,
        content: reply.trim(),
        timestamp: new Date().toISOString(),
        read: true
      }
    ];
    try {
      await updateMessage(selected.id, { thread: nextThread });
      setReply('');
    } catch {
      setError('Your reply could not be sent. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {error && <InlineAlert tone="danger">{error}</InlineAlert>}
      {loading ? <LoadingState label="Loading conversations…" /> : <div className={`domain-inbox ${selected && !showMobileList ? 'has-selection' : ''}`}>
        <aside className="domain-inbox-list" aria-label="Conversations">
          <div className="domain-inbox-controls">
            <SearchField value={query} onChange={event => setQuery(event.target.value)} placeholder="Search conversations…" label="Search conversations" />
            <button type="button" className="btn btn-primary" onClick={() => { setError(''); setComposeOpen(true); }}>New message</button>
          </div>
          <div className="domain-conversations">
            {visibleMessages.map(message => {
              const other = counterpart(message, currentUserId, users);
              const unread = message.recipientId === currentUserId && !message.read;
              return (
                <button
                  type="button"
                  key={message.id}
                  className={`${selectedId === message.id ? 'is-active' : ''} ${unread ? 'is-unread' : ''}`}
                  onClick={() => { setSelectedId(message.id); setShowMobileList(false); }}
                >
                  <span className="domain-conversation-avatar" aria-hidden="true">{other.name.charAt(0).toUpperCase()}</span>
                  <span className="domain-conversation-copy">
                    <span><strong>{other.name}</strong><time>{formatDateTime(message.createdAt || message.timestamp)}</time></span>
                    <b>{message.subject || 'No subject'}</b>
                    <small>{message.content || 'No message content'}</small>
                  </span>
                </button>
              );
            })}
            {visibleMessages.length === 0 && <EmptyState title="No conversations" description={query ? 'Try changing your search.' : 'New conversations will appear here.'} />}
          </div>
        </aside>

        <section className="domain-thread" aria-label="Current conversation">
          {selected ? (
            <>
              <header className="domain-thread-header">
                <button type="button" className="domain-thread-back" onClick={() => setShowMobileList(true)}>← Conversations</button>
                <div>
                  <h2>{counterpart(selected, currentUserId, users).name}</h2>
                  <p>{selected.subject || 'No subject'}</p>
                </div>
              </header>
              <div className="domain-thread-history">
                <article>
                  <header><strong>{selected.senderName || 'Sender'}</strong><time>{formatDateTime(selected.createdAt || selected.timestamp)}</time></header>
                  <p>{selected.content}</p>
                </article>
                {(selected.thread || []).map(item => (
                  <article key={item.id} className={item.senderId === currentUserId ? 'is-mine' : ''}>
                    <header><strong>{item.senderName || (item.senderId === currentUserId ? 'You' : 'Reply')}</strong><time>{formatDateTime(item.timestamp)}</time></header>
                    <p>{item.content}</p>
                  </article>
                ))}
              </div>
              <div className="domain-thread-composer">
                <label htmlFor="message-reply">Reply</label>
                <textarea id="message-reply" value={reply} onChange={event => setReply(event.target.value)} placeholder="Write your reply…" />
                <div><button type="button" className="btn btn-primary" onClick={sendReply} disabled={sending || !reply.trim()}>{sending ? 'Sending…' : 'Send reply'}</button></div>
              </div>
            </>
          ) : <EmptyState title="Select a conversation" description="Choose a conversation from the inbox to read and reply." />}
        </section>
      </div>}

      <Dialog
        open={composeOpen}
        title="New message"
        description="Send a direct academic message."
        onClose={() => setComposeOpen(false)}
        footer={<><button type="button" className="btn btn-secondary" onClick={() => setComposeOpen(false)}>Cancel</button><button type="button" className="btn btn-primary" onClick={sendDraft} disabled={sending || !draft.recipientId || !draft.subject.trim() || !draft.content.trim()}>{sending ? 'Sending…' : 'Send message'}</button></>}
      >
        {error && <InlineAlert tone="danger">{error}</InlineAlert>}
        <div className="ui-form-stack">
          <Field label="To">
            <select value={draft.recipientId} onChange={event => setDraft(previous => ({ ...previous, recipientId: event.target.value }))}>
              <option value="">Select a recipient</option>
              {recipients.map(recipient => <option key={recipient.id} value={recipient.id}>{personName(recipient)} ({recipient.role})</option>)}
            </select>
          </Field>
          <Field label="Subject"><input value={draft.subject} onChange={event => setDraft(previous => ({ ...previous, subject: event.target.value }))} /></Field>
          <Field label="Message"><textarea rows="7" value={draft.content} onChange={event => setDraft(previous => ({ ...previous, content: event.target.value }))} /></Field>
        </div>
      </Dialog>
    </>
  );
}
